import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';

import { AnyPlayerUpcomingGames } from 'components/player/AnyPlayerUpcomingGames';
import type { PlayerResultsProps } from 'searchCommonPlayer/AdvancedPlayerSearch/types';
import { useManagePlayerSearchView } from 'searchCommonPlayer/StatsManager/useManagePlayerSearchView';

import { SearchPlayerCell } from '../SearchPlayerCell';
import { SearchPlayerStatsCells } from '../SearchPlayerStatsCells';
import { AveragePlayerScoreCell } from '../SearchPlayerStatsCells/AveragePlayerScoreCell';
import { CellGrow, SearchTableRow } from '../SearchTable/ui';
import { StartingPriceCell } from '../StartingPriceCell';
import type { SearchPlayerRow_anyPlayer } from './__generated__/index.graphql';
import { ProjectionGradeCell } from '../SearchPlayerStatsCells/ProjectionGradeCell';

type Props = Pick<PlayerResultsProps, 'watchlistId' | 'favoritesWatchlist'> & {
  anyPlayer: SearchPlayerRow_anyPlayer;
  onStatsClick: () => void;
  onListingsClick: () => void;
  rarity: Rarity;
};

export const SearchPlayerRow = ({
  anyPlayer,
  onStatsClick,
  onListingsClick,
  rarity,
  watchlistId,
  favoritesWatchlist,
}: Props) => {
  const { statsManager } = useManagePlayerSearchView();
  return (
    <SearchTableRow gap={2}>
      <SearchPlayerCell
        anyPlayer={anyPlayer}
        onStatsClick={onStatsClick}
        watchlistId={watchlistId}
        favoritesWatchlist={favoritesWatchlist}
      />
      <AnyPlayerUpcomingGames anyPlayer={anyPlayer} />
      {statsManager[PlayerSearchViewAttributes.PROJECTION_GRADE].map(
        so5FixtureEvent => (
          <ProjectionGradeCell
            key={`projectionGrade-${so5FixtureEvent}-${anyPlayer.slug}`}
            so5FixtureEvent={so5FixtureEvent}
            anyPlayer={anyPlayer}
          />
        )
      )}
      {statsManager[PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE].map(
        mode => (
          <AveragePlayerScoreCell
            key={mode}
            mode={mode}
            anyPlayer={anyPlayer}
          />
        )
      )}
      <SearchPlayerStatsCells anyPlayer={anyPlayer} />
      <CellGrow />
      <StartingPriceCell
        anyPlayer={anyPlayer}
        rarity={rarity}
        onListingsClick={onListingsClick}
      />
    </SearchTableRow>
  );
};

SearchPlayerRow.fragments = {
  anyPlayer: gql`
    fragment SearchPlayerRow_anyPlayer on AnyPlayerInterface {
      slug
      ...ProjectionGradeCell_anyPlayer
      ...SearchPlayerStatsCells_anyPlayer
      ...SearchPlayerCell_anyPlayer
      ...StartingPriceCell_anyPlayer
      ...AnyPlayerUpcomingGames_anyPlayer
      ...AveragePlayerScoreCell_anyPlayer
    }
    ${ProjectionGradeCell.fragments.anyPlayer}
    ${AnyPlayerUpcomingGames.fragments.anyPlayer}
    ${SearchPlayerCell.fragments.anyPlayer}
    ${SearchPlayerStatsCells.fragments.anyPlayer}
    ${StartingPriceCell.fragments.anyPlayer}
    ${AveragePlayerScoreCell.fragments.anyPlayer}
  ` as TypedDocumentNode<SearchPlayerRow_anyPlayer>,
};
